/**
 * Sync engine: drains the outbox to the server when online.
 * Designed for the technician PWA workflow.
 */
import api from "@/lib/api";
import {
    listOutbox,
    updateOutbox,
    removeOutbox,
    countPending,
    updateCachedSchedule,
    cacheReports,
    getPendingPhotos,
    removePendingPhoto,
    countPendingPhotos,
    getDb,
} from "@/lib/offlineStore";

let syncing = false;
const listeners = new Set();

function notify() {
    for (const l of listeners) {
        try { l(); } catch {}
    }
}

export function onSyncChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export async function getQueueState() {
    const items = await listOutbox();
    const pendingPhotos = await countPendingPhotos();
    return {
        total: items.length + pendingPhotos,
        pending: items.filter((i) => i.status === "pending").length + pendingPhotos,
        syncing: items.filter((i) => i.status === "syncing").length,
        error: items.filter((i) => i.status === "error").length,
        done: items.filter((i) => i.status === "done").length,
        pendingPhotos,
        items,
    };
}

// Upload MIP photos captured while offline. One POST per photo carries the final
// metadata, so no server-id remapping is needed. Removes each on success.
export async function syncPendingPhotos() {
    if (!navigator.onLine) return;
    const pend = (await getPendingPhotos()).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    for (const p of pend) {
        try {
            await api.post(`/photo-reports/${p.serviceId}/photos`, {
                zone: p.zone,
                area: p.area,
                custom_area: p.custom_area || null,
                description: p.description || "",
                is_reincident: !!p.is_reincident,
                data_uri: p.data_uri,
            }, { timeout: 60000 });
            await removePendingPhoto(p.id);
            notify();
        } catch (err) {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) {
                // Permanent-ish error (e.g. limit reached / service gone): skip this
                // one but keep trying the rest so it doesn't block the queue.
                continue;
            }
            // Network / 5xx -> stop and retry later
            break;
        }
    }
}

async function dispatchOne(item) {
    // Mark syncing
    await updateOutbox(item.id, { status: "syncing", attempts: (item.attempts || 0) + 1 });
    notify();

    try {
        const res = await api.request({
            url: item.url,
            method: item.method || "POST",
            data: item.body,
            timeout: 60000,
        });

        // Apply post-success local updates
        if (item.type === "start_service" && item.scheduleId && res?.data) {
            await updateCachedSchedule(item.scheduleId, {
                status: res.data.status,
                entry_time: res.data.entry_time,
                _sync_pending: false,
            });
        } else if (item.type === "not_done" && item.scheduleId && res?.data) {
            await updateCachedSchedule(item.scheduleId, {
                status: res.data.status,
                not_done_reason: res.data.not_done_reason,
                _sync_pending: false,
            });
        } else if (item.type === "complete_report" && res?.data) {
            // Persist the server report locally so receipt page can show it
            await cacheReports([res.data]);
            if (item.scheduleId) {
                await updateCachedSchedule(item.scheduleId, {
                    status: "completed",
                    exit_time: res.data.exit_time,
                    _sync_pending: false,
                    _server_report_id: res.data.id,
                });
            }
        }

        await removeOutbox(item.id);
        return { ok: true, response: res.data };
    } catch (err) {
        const status = err?.response?.status;
        // Server-side rejection (4xx) -> error state, don't retry blindly
        if (status && status >= 400 && status < 500) {
            await updateOutbox(item.id, {
                status: "error",
                error: err?.response?.data?.detail || err.message || "Error del servidor",
            });
            return { ok: false, error: err };
        }
        // Network / 5xx -> keep pending for retry
        await updateOutbox(item.id, {
            status: "pending",
            error: err?.message || "Sin conexión",
        });
        throw err; // bubble up so the loop stops on network errors
    }
}

export async function drain() {
    if (syncing) return { ok: false, reason: "busy" };
    if (!navigator.onLine) return { ok: false, reason: "offline" };
    syncing = true;
    notify();
    try {
        // Process in insertion order (id ASC), only items that are pending or in error->retry
        const items = (await listOutbox()).sort((a, b) => a.id - b.id);
        const toProcess = items.filter((i) => i.status === "pending" || i.status === "error");
        for (const item of toProcess) {
            try {
                await dispatchOne(item);
            } catch (e) {
                // network error -> stop
                break;
            }
        }
        // Upload any MIP photos captured offline
        try { await syncPendingPhotos(); } catch { /* ignore */ }
    } finally {
        syncing = false;
        notify();
    }
    return { ok: true };
}

let initialized = false;
export function initSyncEngine() {
    if (initialized) return;
    initialized = true;
    const tryDrain = () => { drain(); };
    window.addEventListener("online", tryDrain);
    window.addEventListener("focus", tryDrain);
    // Try once shortly after init in case we already have queued items + connection
    setTimeout(tryDrain, 1500);
    // Periodic retry every 60s
    setInterval(() => { if (navigator.onLine) drain(); }, 60_000);
}

export function isSyncing() { return syncing; }

export { countPending, countPendingPhotos };
